/**
 * Custom Line Items - Helper Products Manager
 * -------------------------------------------
 * Automatically adds embroidery and personalization helper products to cart
 * based on logo and personalization counts from decorated products.
 *
 * NO JQUERY DEPENDENCY - Uses native JavaScript only
 */

(function() {
  'use strict';

  // Flag to prevent infinite loops
  let isUpdating = false;

  /**
   * Initialize event listeners when DOM is ready
   */
  function init() {
    // Listen for various cart events
    document.addEventListener("ajaxProduct:added", handleCartUpdate);
    document.addEventListener("cart:updated", handleCartUpdate);
    document.addEventListener("cart:update", handleCartUpdate);
    // Don't listen to theme:cart:change - we dispatch this ourselves after update
    // document.addEventListener("theme:cart:change", handleCartUpdate);

    // Listen for customizer-v2 cart events (with delay for cart sync)
    document.addEventListener('theme:cart:reload', function(event) {
      // Small delay to ensure cart.js is updated before we fetch it
      setTimeout(handleCartUpdate, 500);
    });
  }

  /**
   * Handle cart update events (with re-entry protection)
   */
  function handleCartUpdate() {
    // Prevent infinite loops
    if (isUpdating) {
      return;
    }
    isUpdating = true;

    addUpdatePersonalizeProduct()
      .then(function(res) {
        // Helper products updated
      })
      .catch(function(err) {
        // Error updating helper products
      })
      .finally(function() {
        // Reset flag after a delay to allow cart to settle
        setTimeout(function() {
          isUpdating = false;
        }, 1000);
      });
  }

  /**
   * Fetch Product Variant by Handle
   * Uses native fetch instead of jQuery.getJSON
   */
  async function getProductsByHandle(product_handle, res_type, type, sku) {
    try {
      const response = await fetch('/products/' + product_handle + '.js');
      if (!response.ok) {
        throw new Error('Failed to fetch product: ' + response.status);
      }
      const product = await response.json();

      let resProduct = "";

      if (res_type === 'id') {
        // Return variant ID only
        if (type === 'first') {
          resProduct = product.variants[0].id;
        } else if (type === 'sku' && sku !== '') {
          for (const item of product.variants) {
            if (item.sku === sku) {
              resProduct = item.id;
              break;
            }
          }
        }
      } else {
        // Return variant object with additional product info
        if (type === 'first') {
          const variantsData = product.variants[0];
          variantsData.product_id = product.id;
          variantsData.variant_id = variantsData.id;
          variantsData.name = product.title;
          variantsData.title = product.title;
          variantsData.handle = product.handle;
          variantsData.vendor = product.vendor;
          variantsData.images = { "src": "https:" + product.featured_image };
          resProduct = variantsData;
        } else if (type === 'sku' && sku !== '') {
          for (const item of product.variants) {
            if (item.sku === sku) {
              item.product_id = product.id;
              item.variant_id = item.id;
              item.handle = product.handle;
              item.vendor = product.vendor;
              item.images = { "src": "https:" + product.featured_image };
              resProduct = item;
              break;
            }
          }
        }
      }

      if (resProduct === '') {
        resProduct = product; // fallback to full product
      }

      return resProduct;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if an object is empty (replacement for $.isEmptyObject)
   */
  function isEmptyObject(obj) {
    return obj === null || obj === undefined || Object.keys(obj).length === 0;
  }

  /**
   * Add / Update Personalization Products in Cart
   * ---------------------------------------------
   * Main function to:
   *   - Read current cart contents
   *   - Count logos and personalization entries from line item properties
   *   - Calculate how many embroidery/additional logo/personalization products are required
   *   - Add or update those helper products in the cart
   *
   * Handles logic for pricing tiers:
   *   - First logo tier (12.5, 7.5, 2.5 depending on quantity break)
   *   - Additional logo locations
   *   - Personalization locations
   */
  async function addUpdatePersonalizeProduct() {
    try {
      // Fetch current cart state
      const res = await fetch((window.Shopify?.routes?.root ?? "/") + "cart.js");
      const cart = await res.json();

      let logoLocationFirst = 0;
      let additionalLogoLocation = 0;
      let personalizaLogoLocation = 0;

      // Parse _customizations from cart attributes (source of truth)
      let customizations = {};
      if (cart.attributes && cart.attributes._customizations) {
        try {
          customizations = typeof cart.attributes._customizations === 'string'
            ? JSON.parse(cart.attributes._customizations)
            : cart.attributes._customizations;
        } catch (e) {
          // Failed to parse _customizations
        }
      }

      // Process each cart item
      cart.items.forEach(item => {
        const quantity = parseInt(item.quantity);
        const properties = item.properties;

        // Skip embroidery and helper products, only process decorated products
        if (item.handle !== "embroidery" &&
            item.handle !== "additional-logo-locations" &&
            item.handle !== "personalization" &&
            !isEmptyObject(properties)) {

          let logoCount = 0;
          let personalizationCount = 0;

          // Try to get counts from _customizations first (source of truth)
          const variantId = item.variant_id;
          let foundInCustomizations = false;

          // Look for matching variant key in _customizations
          for (const [key, data] of Object.entries(customizations)) {
            if (key.startsWith(`variant_${variantId}_`)) {
              foundInCustomizations = true;
              if (data.locations && Array.isArray(data.locations)) {
                data.locations.forEach(loc => {
                  if (loc.logo && loc.logo.id) {
                    logoCount++;
                  }
                  if (loc.personalization && loc.personalization.text && loc.personalization.text.trim()) {
                    personalizationCount++;
                  }
                });
              }
              break; // Found matching customization, stop searching
            }
          }

          // Fallback: count from line item properties (legacy items)
          if (!foundInCustomizations) {
            Object.keys(properties).forEach(logoKey => {
              const logoValue = properties[logoKey];

              // Count logos: match "Logo" or "Logo N" pattern (not "Logo N PNG" or other suffixes)
              const logoKeyLower = logoKey.toLowerCase();
              if (logoKeyLower === 'logo' || /^logo\s+\d+$/.test(logoKeyLower)) {
                if (logoValue && logoValue.toString().trim() !== "") {
                  logoCount++;
                }
              }

              // Count personalization: match "_personalizationN_coordinates" or "_personalizationNCoordinates" patterns
              if (logoKeyLower.startsWith("_personalization") &&
                  logoKeyLower.includes("coordinates") &&
                  logoValue &&
                  logoValue.toString().trim() !== "") {
                personalizationCount++;
              }
            });
          }

          // Add counts to total tracking
          if (logoCount > 0) {
            if (logoCount > 1) {
              logoLocationFirst += quantity;
              additionalLogoLocation += quantity * (logoCount - 1);
            } else {
              logoLocationFirst += quantity;
            }
          }

          if (personalizationCount > 0) {
            personalizaLogoLocation += quantity * personalizationCount;
          }
        }
      });

      // Apply tiered pricing logic for embroidery product
      let logoLocationFirst12_5 = 0;
      let logoLocationFirst7_5 = 0;
      let logoLocationFirst2_5 = 0;

      if (logoLocationFirst === 1) {
        logoLocationFirst12_5 = logoLocationFirst;
      } else if (logoLocationFirst > 1 && logoLocationFirst < 72) {
        logoLocationFirst7_5 = logoLocationFirst;
      } else if (logoLocationFirst >= 72) {
        // 72+ locations get the lowest tier
        logoLocationFirst2_5 = logoLocationFirst;
      }

      // Fetch required helper product variant IDs
      const [logoLocationFirst12_5_id, logoLocationFirst7_5_id, logoLocationFirst2_5_id, additionalLogoLocation_id, personalizaLogoLocation_id] = await Promise.all([
        getProductsByHandle("embroidery", "id", "sku", "EMB-1"),
        getProductsByHandle("embroidery", "id", "sku", "EMB-2"),
        getProductsByHandle("embroidery", "id", "sku", "EMB-3"),
        getProductsByHandle("additional-logo-locations", "id", "first", ""),
        getProductsByHandle("personalization", "id", "first", "")
      ]);

      // Build response object with IDs and quantities
      const response = {
        "logoLocationFirst12_5": { "id": logoLocationFirst12_5_id, "quantity": logoLocationFirst12_5 },
        "logoLocationFirst7_5": { "id": logoLocationFirst7_5_id, "quantity": logoLocationFirst7_5 },
        "logoLocationFirst2_5": { "id": logoLocationFirst2_5_id, "quantity": logoLocationFirst2_5 },
        "additionalLogoLocation": { "id": additionalLogoLocation_id, "quantity": additionalLogoLocation },
        "personalizaLogoLocation": { "id": personalizaLogoLocation_id, "quantity": personalizaLogoLocation }
      };

      // Format for Shopify cart/update.js endpoint
      // Include all variant IDs (0 quantity removes items)
      const updates = {};
      Object.keys(response).forEach(key => {
        const product = response[key];
        if (product.id) {
          updates[product.id.toString()] = product.quantity;
        }
      });

      // Push updates to Shopify cart
      if (Object.keys(updates).length > 0) {
        const updateRes = await fetch((window.Shopify?.routes?.root ?? "/") + "cart/update.js", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ updates: updates })
        });

        if (updateRes.ok) {
          await updateRes.json();

          // Trigger cart drawer rebuild (theme listens to this)
          // The isUpdating flag will prevent infinite loops
          setTimeout(function() {
            document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          }, 100);
        }
      }

      return response;
    } catch (error) {
      return null;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
